<template>
  <form-dropdown
    :label="label"
    :placeholder="placeholder"
    :classes="classes"
    :disabled="disabled"
    :is-update-in-process="planetStore.isUpdateInProcess"
    :ignore-update-in-process="ignoreUpdateInProcess"
    :required="required"
    :is-invalid="isInvalid"
    :error-message-text="errorMessageText"
    :type="type"
    :can-clear-single-select="canClearSingleSelect"
    :all-visible="allVisible"
    :filter-force-display="filterForceDisplay"
    :filter-variant="filterVariant"
    :items="items"
    :value="value"
    :list-position="listPosition"
    :all-label="allLabel"
    :all-disabled="allDisabled"
    :all-checked-override="allCheckedOverride"
    :all-checked-override-value="allCheckedOverrideValue"
    :all-checked-override-indeterminate="allCheckedOverrideIndeterminate"
    :toggle-value="toggleValue"
    :toggle-label="toggleLabel"
    :toggle-disabled-value="toggleDisabled"
    :virtual-scroll="virtualScroll"
    :header-tooltip="headerTooltip"
    :tag-click-enabled="tagClickEnabled"
    :skip-sort="skipSort"
    :exclusive-option-ids="exclusiveOptionIds"
    :custom-container="customContainer"
    ref="formDropdownRef"
    v-bind="$attrs"
    @update:search-value="emit('update:searchValue', $event)"
    @update:toggleValue="emit('update:toggleValue', $event)"
    @update:value="emit('update:value', $event)"
    @is-active="emit('isActive', $event)"
    @search="emit('search', $event)"
    @is-open="emit('isOpen', $event)"
  >
    <template #labelOverride v-if="$slots.labelOverride">
      <slot name="labelOverride"></slot>
    </template>
    <template #postLabel v-if="$slots.postLabel">
      <slot name="postLabel"></slot>
    </template>
    <template #headerContentOverride="props" v-if="$slots.headerContentOverride">
      <slot name="headerContentOverride" :checkedItems="props.checkedItems" :items="items"> </slot>
    </template>
    <template #headerCustomTagOverride="props" v-if="$slots.headerCustomTagOverride">
      <slot
        name="headerCustomTagOverride"
        :checkedItems="props.checkedItems"
        :tagClickEnabled="props.tagClickEnabled"
      >
      </slot>
    </template>
  </form-dropdown>
</template>

<script setup lang="ts">
import FormDropdown from 'ui-component-library/rbz/components/FormDropdown.vue';
import { ListItemType } from 'ui-component-library/rbz/components/MenuListItem.vue';
import { DropdownItem } from '@/types';
import { usePlanetStore } from '@/store/PlanetStore';
import { ref, computed } from 'vue';

export type ListPosition = 'bottom' | 'top';

interface RbzDropdownProps {
  label?: string;
  placeholder?: string;
  classes?: string;
  disabled?: boolean;
  ignoreUpdateInProcess?: boolean;
  required?: boolean;
  isInvalid?: boolean;
  errorMessageText?: string;
  type?: ListItemType;
  canClearSingleSelect?: boolean;
  allVisible?: boolean;
  filterForceDisplay?: boolean;
  filterVariant?: 'default' | 'search-trigger';
  items: DropdownItem[] | [string | number, string][];
  value?: string | string[] | number | number[];
  listPosition?: ListPosition;
  allLabel?: string;
  allDisabled?: boolean;
  allCheckedOverride?: boolean;
  allCheckedOverrideValue?: boolean;
  allCheckedOverrideIndeterminate?: boolean;
  toggleValue?: boolean;
  toggleLabel?: string;
  toggleDisabled?: boolean;
  virtualScroll?: boolean;
  headerTooltip?: string;
  tagClickEnabled?: boolean;
  skipSort?: boolean;
  exclusiveOptionIds?: string[];
  customContainer?: HTMLElement | null;
}

const props = withDefaults(defineProps<RbzDropdownProps>(), {
  placeholder: null,
  errorMessageText: null,
  required: false,
  disabled: false,
  filterForceDisplay: false,
  filterVariant: 'default',
  type: 'menu',
  canClearSingleSelect: false,
  allVisible: true,
  allLabel: null,
  virtualScroll: false,
  tagClickEnabled: true,
  skipSort: false,
  exclusiveOptionIds: (): string[] => [],
  customContainer: null,
});

interface RbzDropdownEmits {
  (e: 'update:value', value: string[] | string): void;

  (e: 'search', value: string): void;

  (e: 'isActive', value: boolean): void;

  (e: 'isOpen', value: boolean): void;

  (e: 'update:toggleValue', value: boolean): void;

  (e: 'update:searchValue', value: string): void;
}

const emit = defineEmits<RbzDropdownEmits>();
const planetStore = usePlanetStore();

const formDropdownRef = ref<InstanceType<typeof FormDropdown> | null>(null);

defineExpose({
  toggleDropdown: () => formDropdownRef.value?.toggleDropdown(),
  focusDropdown: () => formDropdownRef.value?.focusDropdown(),
  dropdownListRef: computed(() => formDropdownRef.value?.dropdownListRef),
});
</script>
